import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStateValue } from "../../StateProvider";
import { useTitle } from "../../hooks/useTitle";
import { tablesDB, DB_ID, ORDERS_TABLE } from "../../appwrite";
import { Query } from "appwrite";
import NoOrder from "./NoOrder";
import Navbar from "../../Components/Navbar/Navbar";
import Footer from "../../Components/Footer/Footer";
import "./OrderList.css";
import Loading from "../../Loading";

const fmtStatus = (s) =>
  (s || "pending").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const formatPrice = (price) => {
  if (isNaN(price)) return "Invalid Price";
  return parseFloat(price).toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function OrderList() {
  useTitle("Your Orders - TrendBay DS");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [{ user }] = useStateValue();
  const navigate = useNavigate();
  const ORDERS_PER_PAGE = 5;

  useEffect(() => {
    if (!user) {
      navigate("/login");
    } else {
      fetchOrders(false);
    }
  }, [user, navigate]);

  const fetchOrders = async (loadMore = false) => {
    if (!user) return;

    setLoading(true);

    try {
      // Build queries for Appwrite - query by userId
      const queries = [
        Query.equal("userId", user.$id),
        Query.orderDesc("$createdAt"), // Make sure you have a createdAt field
        Query.limit(ORDERS_PER_PAGE),
      ];

      if (loadMore) {
        queries.push(Query.offset(offset));
      }

      // Fetch orders from Appwrite
      const response = await tablesDB.listRows({
        databaseId: DB_ID,
        tableId: ORDERS_TABLE,
        queries: queries,
      });

      const ordersData = response.rows || response.documents || [];

      const formattedOrders = ordersData.map((row) => ({
        ...row,
        orderId: row.$id || row.orderId, // Use both possible ID sources
      }));

      if (loadMore) {
        setOrders((prevOrders) => [...prevOrders, ...formattedOrders]);
      } else {
        setOrders(formattedOrders);
      }

      // Check if there are more orders to load
      setHasMore(ordersData.length === ORDERS_PER_PAGE);
      setOffset(loadMore ? offset + ordersData.length : ordersData.length);
    } catch (error) {
      // silently handle error
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "Unknown date";

    // Handle different date formats
    try {
      if (typeof dateValue === "object" && dateValue.seconds) {
        return new Date(dateValue.seconds * 1000).toLocaleDateString();
      } else if (typeof dateValue === "string") {
        return new Date(dateValue).toLocaleDateString();
      } else if (typeof dateValue === "number") {
        return new Date(dateValue).toLocaleDateString();
      } else {
        return "Invalid date";
      }
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <div>
      <Navbar />
      <div className="ol-page">
        <div className="ol-wrap">
          <h1 className="ol-title">Your Orders</h1>
          {loading && orders.length === 0 ? (
            <Loading />
          ) : orders.length > 0 ? (
            <>
              {/* Desktop table */}
              <table className="order-table ol-desktop">
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr
                      key={order.orderId || order.$id}
                      className="ol-row"
                      onClick={() => navigate(`/order/${order.orderId || order.$id}`)}
                    >
                      <td className="ol-order-num">#{order.orderNumber || (order.orderId || order.$id).slice(-8)}</td>
                      <td>{formatDate(order.$createdAt || order.fulfillmentStatusUpdatedAt)}</td>
                      <td>₦{formatPrice(order.orderTotal)}</td>
                      <td>
                        <span className={`ol-tbl-badge ol-tbl-badge--${(order.fulfillmentStatus || "pending").toLowerCase().replace(/\s+/g, "-")}`}>
                          {fmtStatus(order.fulfillmentStatus)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile card list */}
              <div className="ol-cards">
                {orders.map((order) => {
                  const id = order.orderId || order.$id;
                  const fulfil = order.fulfillmentStatus || "pending";
                  return (
                    <div className="ol-card" key={id}>
                      <div className="ol-card__top">
                        <span className="ol-card__id">
                          #{order.orderNumber || id.slice(-8)}
                        </span>
                        <span className={`ol-card__status ol-card__status--${fulfil.toLowerCase().replace(/\s+/g, "-")}`}>
                          {fmtStatus(fulfil)}
                        </span>
                      </div>
                      <div className="ol-card__meta">
                        <span>{formatDate(order.$createdAt || order.fulfillmentStatusUpdatedAt)}</span>
                        <strong>₦{formatPrice(order.orderTotal)}</strong>
                      </div>
                      <button className="ol-card__btn" onClick={() => navigate(`/order/${id}`)}>
                        View Order →
                      </button>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <div className="ol-load-more">
                  <button onClick={() => fetchOrders(true)} disabled={loading}>
                    {loading ? "Loading…" : "Load More"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <NoOrder />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default OrderList;
